import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, TransactionsLists } from 'vopay';
import { BodyText } from 'UI';
import ErrorInfoCard from '@/components/Entities/UI/ErrorInfoCard/ErrorInfoCard';
import TransactionCard from '@/components/Entities/UI/TransactionCard/TransactionCard';
import TransactionDetailsCard from '@/components/Entities/UI/TransactionDetailsCard/TransactionDetailsCard';

type GenericTransactionContainerProps = {
  /** The row the page read; undefined while it loads, null when it could not be read. */
  transaction: TransactionsLists | null | undefined;
};

/**
 * The detail view for a rail the page has no dedicated container for (eLinx, ACH, VoPay Instant, …):
 * the row as the account's transaction list returns it, so every row on the account page's
 * Transactions tab opens something. The dedicated containers stay the richer view for their rails.
 */
export default function GenericTransactionContainer({ transaction }: GenericTransactionContainerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.Details.GenericTransaction' });
  // The rail views' own labels; this view shows the same fields under the same names, so it reads them
  // from there rather than declaring a second copy of each string.
  const { t: tShared } = useTranslation('translations', { keyPrefix: 'Transaction.Details.TransactionDetails' });
  const { accountId } = useParams<{ accountId: string }>();
  const accountName = new URLSearchParams(useLocation().search).get('accountName') || '';

  if (transaction === undefined) return <Skeleton height={320} data-testid="transactionDetailLoading" />;

  if (transaction === null) {
    return (
      <BodyText data-testid="transactionDetailNotFound">
        {t('notFound', { defaultValue: 'This transaction is not available.' })}
      </BodyText>
    );
  }

  const amount = Number(transaction.CreditAmount) > 0 ? transaction.CreditAmount : transaction.DebitAmount;

  const details = [
    {
      label: tShared('Label.transactionType', { defaultValue: 'Transaction Type' }),
      value: transaction.TransactionType,
      'data-testid': 'detail-transactionType',
    },
    {
      label: t('Label.status', { defaultValue: 'Status' }),
      value: transaction.TransactionStatus || '-',
      'data-testid': 'detail-status',
    },
    {
      label: tShared('Label.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime || '-',
      'data-testid': 'detail-transactionDate',
    },
    {
      label: tShared('Label.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified || '-',
      'data-testid': 'detail-lastModified',
    },
    {
      label: t('Label.settlementDate', { defaultValue: 'Settlement Date' }),
      value: transaction.SettlementDate || '-',
      'data-testid': 'detail-settlementDate',
    },
    {
      label: t('Label.accountName', { defaultValue: 'Account Name' }),
      value: transaction.AccountName || '-',
      'data-testid': 'detail-accountName',
    },
    {
      label: tShared('Label.clientAccountId', { defaultValue: 'Client Account ID' }),
      value: transaction.ClientAccountID || '-',
      'data-testid': 'detail-clientAccountId',
    },
    {
      label: tShared('Label.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
      'data-testid': 'detail-clientReferenceNumber',
    },
    {
      label: t('Label.paymentRailId', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
      'data-testid': 'detail-paymentRailId',
    },
    {
      label: t('Label.elinxId', { defaultValue: 'eLinx ID' }),
      value: transaction.ELinxRequestID || '-',
      'data-testid': 'detail-elinxId',
    },
    {
      label: tShared('Label.scheduledTransactionId', { defaultValue: 'Scheduled Transaction ID' }),
      value: transaction.ScheduledTransactionID || '-',
      'data-testid': 'detail-scheduledTransactionId',
    },
    {
      label: t('Label.parentTransactionId', { defaultValue: 'Parent Transaction ID' }),
      value: transaction.ParentTransactionID || '-',
      'data-testid': 'detail-parentTransactionId',
    },
    {
      label: tShared('Label.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount || '-',
      'data-testid': 'detail-holdAmount',
    },
    {
      label: t('Label.convenienceFee', { defaultValue: 'Convenience Fee' }),
      value: transaction.ConvenienceFeeAmount || '-',
      'data-testid': 'detail-convenienceFee',
    },
    {
      label: tShared('Label.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
      'data-testid': 'detail-glCode',
    },
    {
      label: t('Label.flag', { defaultValue: 'Flag' }),
      value: transaction.TransactionFlag || '-',
      'data-testid': 'detail-flag',
    },
    {
      label: tShared('Label.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
      'data-testid': 'detail-notes',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={transaction.FullName || accountName || '-'}
        amount={amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={transaction.TransactionType}
        accountId={accountId}
        failureReason={transaction.TransactionFailureReason || undefined}
      />

      <TransactionDetailsCard
        title={tShared('Title.transactionDetails', { defaultValue: 'Transaction Details' })}
        details={details}
      />

      {/* Last, as every rail's own view places it; the card renders nothing without a failure reason. */}
      <ErrorInfoCard
        currency={transaction.Currency}
        amount={amount}
        lastModified={transaction.LastModified}
        failureReason={transaction.TransactionFailureReason}
      />
    </Box>
  );
}
